<template>
  <div>
    <my-header></my-header>

    <div class="content-container-level-1">
      <h2>Edit timetable record</h2>

      <form @submit.prevent="updateRecordWithConfirmation">
<!--        <div class="form-group">-->
<!--          <label for="riverRumble">River rumble</label>-->
<!--          <input v-model="riverRumbleAlias" id="riverRumble" class="form-control" type="string" disabled>-->
<!--        </div>-->

<!--        <hr>-->

        <div class="form-group">
          <label for="start-at">Start at:</label>
          <date-time-input id="start-at" v-model="startAt"></date-time-input>
        </div>

        <hr>

        <div class="form-group">
          <h5>Duration</h5>
          <duration-input v-model="duration"></duration-input>
          <p class="mb-3">
            End at<br>
            {{ endAt | getMomentStr }}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="seed">Seed</label>
          <div class="d-flex seed-container">
            <div class="d-flex flex-column flex-grow-1">
              <input v-model.number="seed" id="seed" class="form-control" type="number" min="0">
            </div>
            <div class="d-flex flex-column">
              <b-button variant="primary" @click="randomizeSeed">Randomize</b-button>
            </div>
          </div>
        </div>

        <hr>

        <div class="form-group">
          <label for="minLevel">Min level</label>
          <input v-model="minLevel" id="minLevel" class="form-control" type="number" min="0">
        </div>

        <hr>

        <div class="form-group">
          <h5>Min time until end for start</h5>
          <duration-input v-model="minTimeUntilEndForStart"></duration-input>
          <p class="mb-3">
            Start available until<br>
            {{ startAvailableUntil | getMomentStr }}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="minClientVersion">Min client version</label>
          <input v-model="minClientVersion" id="minClientVersion" class="form-control">
          <p class="form-text">
            Min value (forced version): {{ forcedVersion }}
          </p>
          <p class="form-text">
            Max value (server version): {{ serverVersion }}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="playerGroupId">Segment:</label>
          <b-form-select
              id="playerGroupId"
              v-model="playerGroupId"
              :options="playerGroupIdOptions"
          />
        </div>

        <hr>

        <b-button :to="{name: 'river_rumble_timetable'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Save</b-button>
      </form>
    </div>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import {confirmAction} from '../../service/confirm';

import {convertVersionCodeToStr, getMomentStr, getProtoAlias,} from '../../util';

import notifier from '../../notifier';
import {HTTP} from '../../http-common';

import MyHeader from '../Header.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import DateTimeInput from '../DateTimeInput.vue';
import DurationInput from '../DurationInput.vue';
import {Fnv32a, Random} from "@/model/river_rumble";

import {DEFAULT_PLAYER_GROUP_ID, getPlayerGroupOptionsVerbose} from '../../model/event.js';

export default {
  name: 'EditRiverRumbleTimetableRecord',

  components: {
    MyHeader,
    LoadingIndicator,
    DurationInput,
    DateTimeInput,
  },

  props: {
    recordId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,

      // seedRandomizer sequence is dependent on initStamp;
      // repeat creation depends on hash(initStamp, seed)
      initStamp: 0,
      seedRandomizer: null,

      riverRumbleAlias: '',

      playerGroupIdOptions: getPlayerGroupOptionsVerbose(),

      startAt: 0,
      duration: 0,
      seed: 0,
      minLevel: 0,
      minTimeUntilEndForStart: 0,

      forcedVersion: '',
      serverVersion: '',
      minClientVersion: '',
      playerGroupId: DEFAULT_PLAYER_GROUP_ID,
    };
  },

  computed: {
    endAt() {
      return this.startAt / 1000 + this.duration;
    },

    startAvailableUntil() {
      return this.endAt - this.minTimeUntilEndForStart;
    },
  },

  created() {
    this.initStamp = Date.now() / 1000;
    this.initSeedRandomizer();
    this.loadRecord();
  },

  methods: {
    loadRecord() {
      notifier.deleteSenderNotifications(this);

      this.isLoading = true;

      const url = `/river_rumble/timetable/${this.recordId}`;
      HTTP.get(url)
          .then(response => {
            const data = response.data.data;
            if (data === null) {
              notifier.showError('Invalid server response. data === null.', {sender: this});
              return;
            }

            const record = data.record;
            this.initRecordFormFields(record);

            this.forcedVersion = convertVersionCodeToStr(data.forced_version);
            this.serverVersion = convertVersionCodeToStr(data.server_version);
          })
          .catch(e => {
            notifier.showError(e, {sender: this});
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    initRecordFormFields(record) {
      this.riverRumbleAlias = getProtoAlias(record.proto_id);
      this.startAt = record.stime * 1000;
      this.duration = record.duration;
      this.seed = record.seed;
      this.minLevel = record.min_level;
      this.minTimeUntilEndForStart = record.min_time_until_end_for_start;
      this.minClientVersion = convertVersionCodeToStr(record.min_client_version);
      this.playerGroupId = record.player_group_id;
    },

    updateRecordWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const record = this.makeRecord();

      this.confirmRecordUpdating(() => {
        this.doUpdate(record);
      });
    },

    makeRecord() {
      return {
        id: this.recordId,
        startAt: this.startAt / 1000,
        duration: this.duration,
        seed: this.seed,
        minLevel: this.minLevel,
        minTimeUntilEndForStart: this.minTimeUntilEndForStart,
        minClientVersion: this.minClientVersion,
        playerGroupId: this.playerGroupId,
      };
    },

    confirmRecordUpdating(onConfirmed) {
      confirmAction(
          this,
          'Are you sure you want to update this record {env}?',
          'Edit',
          onConfirmed,
      );
    },

    doUpdate(record) {
      const data = new FormData();
      data.set('stime', record.startAt);
      data.set('duration', record.duration);
      data.set('seed', record.seed);
      data.set('min_level', record.minLevel);
      data.set('min_time_until_end_for_start', record.minTimeUntilEndForStart);
      data.set('min_client_version', record.minClientVersion);
      data.set('player_group_id', record.playerGroupId);

      HTTP.post(`/river_rumble/timetable/${record.id}`, data)
          .then(response => {
            const result = response.data.result;

            const RESULT_SUCCESS = 1;
            if (result.code === RESULT_SUCCESS) {
              notifier.showSuccessAfterTransition('River rumble schedule edited.');
              this.$router.push({name: 'river_rumble_timetable'});
              return;
            }

            notifier.showError(result.message, {sender: this});
          })
          .catch(e => {
            notifier.showError(e, {sender: this});
          });
    },

    initSeedRandomizer() {
      const randSeed = new Fnv32a();
      randSeed.writeUint32(this.initStamp);

      this.seedRandomizer = new Random(randSeed.value);
    },
    randomizeSeed() {
      this.seed = this.seedRandomizer.next() % 10000
    }
  },

  filters: {
    getMomentStr,
  },
};
</script>

<style scoped>
.seed-container {
  gap: 1em;
}
</style>
